import { ref, watch } from 'vue';

export const MAX_FRAMES_PER_REFRESH = 16;

export function usePlayback(roundData, onFrameUpdate, onFrameAdvance) {
  let frameAdvanceCallback = onFrameAdvance;

  function setFrameAdvanceCallback(callback) {
    frameAdvanceCallback = callback;
  }
  const currentFrame = ref(0);
  const currentTick = ref(0);
  const previousTick = ref(0);
  const sliderFrame = ref(0);
  const playDemo = ref(false);
  const frameRate = ref(64);
  const intervalId = ref(null);
  const animationFrameId = ref(null);
  const lastAnimationTime = ref(null);
  const sliderActive = ref(false);
  const pendingTickUpdate = ref(null);
  const interpolationProgress = ref(0);
  const lastUpdateFrame = ref(-1);
  const roundEnded = ref(false);

  watch(currentTick, (newVal, oldVal) => {
    if (roundData.value && currentTick.value > roundData.value.endTick) {
      roundEnded.value = true;
    } else {
      roundEnded.value = false;
    }
  });

  function togglePlay() {
    if (playDemo.value) {
      stopPlay();
    } else {
      if (!roundData.value || !roundData.value.frames || roundData.value.frames.length === 0) {
        return;
      }

      playDemo.value = true;
      clearInterval(intervalId.value);

      const frameInterval = 1000 / frameRate.value;
      let lastFrameTime = performance.now();
      lastAnimationTime.value = performance.now();

      function playFrame(currentTime) {
        if (!playDemo.value) {
          animationFrameId.value = null;
          return;
        }

        if (!roundData.value || !roundData.value.frames) {
          stopPlay();
          return;
        }

        const elapsed = currentTime - lastFrameTime;

        if (elapsed >= frameInterval) {
          const lastFrame = roundData.value.frames.length - 1;
          if (currentFrame.value < lastFrame) {
            const steps = Math.min(Math.floor(elapsed / frameInterval), MAX_FRAMES_PER_REFRESH);
            currentFrame.value = Math.min(lastFrame, currentFrame.value + steps);
            sliderFrame.value = currentFrame.value;
            if (frameAdvanceCallback) {
              frameAdvanceCallback();
            }
            lastFrameTime = currentTime - (elapsed % frameInterval);
            interpolationProgress.value = 0;
          } else {
            stopPlay();
            return;
          }
        }

        if (currentFrame.value < roundData.value.frames.length - 1) {
          interpolationProgress.value = Math.min(1, elapsed / frameInterval);
        } else {
          interpolationProgress.value = 0;
        }

        if (onFrameUpdate) {
          onFrameUpdate();
        }

        animationFrameId.value = requestAnimationFrame(playFrame);
      }

      animationFrameId.value = requestAnimationFrame(playFrame);
    }
  }

  function stopPlay() {
    playDemo.value = false;
    clearInterval(intervalId.value);
    intervalId.value = null;
    if (animationFrameId.value !== null) {
      cancelAnimationFrame(animationFrameId.value);
      animationFrameId.value = null;
    }
  }

  function updateFrameRate() {
    const wasPlaying = playDemo.value;
    stopPlay();
    if (frameRate.value * 2 > 256) {
      frameRate.value = 8;
    } else {
      frameRate.value *= 2;
    }
    if (onFrameUpdate) {
      onFrameUpdate();
    }
    if (wasPlaying) togglePlay();
  }

  function increaseFrameRate() {
    const wasPlaying = playDemo.value;
    stopPlay();
    if (frameRate.value * 2 > 256) {
      frameRate.value = 8;
    } else {
      frameRate.value *= 2;
    }
    if (onFrameUpdate) {
      onFrameUpdate();
    }
    if (wasPlaying) togglePlay();
  }

  function decreaseFrameRate() {
    const wasPlaying = playDemo.value;
    stopPlay();
    if (frameRate.value / 2 < 8) {
      frameRate.value = 256;
    } else {
      frameRate.value /= 2;
    }
    if (onFrameUpdate) {
      onFrameUpdate();
    }
    if (wasPlaying) togglePlay();
  }

  function handleSliderMouseDown() {
    sliderActive.value = true;
    lastUpdateFrame.value = -1;
    if (pendingTickUpdate.value !== null) {
      cancelAnimationFrame(pendingTickUpdate.value);
      pendingTickUpdate.value = null;
    }
  }

  function handleSliderMouseUp(onSliderUpdate) {
    sliderActive.value = false;
    if (pendingTickUpdate.value !== null) {
      cancelAnimationFrame(pendingTickUpdate.value);
      pendingTickUpdate.value = null;
    }
    if (roundData.value) {
      const targetFrame = Math.max(0, Math.min(Math.round(sliderFrame.value), roundData.value.frames.length - 1));
      currentFrame.value = targetFrame;
      lastUpdateFrame.value = -1;
      if (onSliderUpdate) {
        onSliderUpdate();
      }
    }
  }

  watch(sliderFrame, (newVal) => {
    if (sliderActive.value) {
      const targetFrame = Math.max(0, Math.min(Math.round(newVal), roundData.value?.frames?.length - 1 || 0));

      if (targetFrame === lastUpdateFrame.value) {
        return;
      }

      currentFrame.value = targetFrame;
      lastUpdateFrame.value = targetFrame;

      // Reset interpolation progress when scrubbing
      interpolationProgress.value = 0;

      // Update currentTick from the new frame immediately
      if (roundData.value && roundData.value.frames && roundData.value.frames[targetFrame]) {
        previousTick.value = currentTick.value;
        currentTick.value = roundData.value.frames[targetFrame].tick;
      }

      if (pendingTickUpdate.value !== null) {
        cancelAnimationFrame(pendingTickUpdate.value);
      }

      pendingTickUpdate.value = requestAnimationFrame(() => {
        if (onFrameUpdate) {
          onFrameUpdate();
        }
        pendingTickUpdate.value = null;
      });
    }
  });

  function handleKeyPress(e, handlers) {
    if (e.code === 'Space') {
      // Space belongs to a focused checkbox or text field; the range slider keeps the play shortcut
      const el = e.target;
      if (el?.isContentEditable || ['TEXTAREA', 'SELECT'].includes(el?.tagName)
        || (el?.tagName === 'INPUT' && el.type !== 'range')) return;
      e.preventDefault();
      togglePlay();
    } else if (e.code === 'KeyN') {
      if (handlers && handlers.onNextRound) {
        handlers.onNextRound();
      }
    } else if (e.code === 'KeyP') {
      if (handlers && handlers.onPrevRound) {
        handlers.onPrevRound();
      }
    } else if (e.code === 'ArrowRight') {
      const skipFrames = frameRate.value * 2;
      currentFrame.value = Math.min(currentFrame.value + skipFrames, roundData.value?.frames?.length - 1 || 0);
      sliderFrame.value = currentFrame.value;
      if (onFrameUpdate) {
        onFrameUpdate();
      }
    } else if (e.code === 'ArrowLeft') {
      const skipFrames = frameRate.value * 2;
      currentFrame.value = Math.max(currentFrame.value - skipFrames, 0);
      sliderFrame.value = currentFrame.value;
      if (onFrameUpdate) {
        onFrameUpdate();
      }
    } else if (e.code === 'KeyF') {
      increaseFrameRate();
    } else if (e.code === 'KeyS') {
      decreaseFrameRate();
    } else if (e.code === 'KeyM') {
      if (handlers && handlers.onToggleMap) {
        handlers.onToggleMap();
      }
    }
  }

  return {
    currentFrame,
    currentTick,
    previousTick,
    sliderFrame,
    playDemo,
    frameRate,
    interpolationProgress,
    roundEnded,
    togglePlay,
    stopPlay,
    updateFrameRate,
    increaseFrameRate,
    decreaseFrameRate,
    handleSliderMouseDown,
    handleSliderMouseUp,
    handleKeyPress,
    setFrameAdvanceCallback,
  };
}
